import { useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/styles';

import type { TActionP } from '@altirllc/shared/utils/types/common';

import type { TSxStyles } from '@/types/common';

type TCarouselProps<TPane extends string> = {
  panes: TPane[];
  sx: TSxStyles;
  children: (pane: TPane, activate: TActionP<TPane>) => JSX.Element;
};

const Wrapper = styled(TransitionGroup)({
  position: 'relative',
  width: '100%',
  height: '100%',
});

const Pane = styled('div')(({ theme }) => ({
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,

  '&.enter-active, &.exit-active': {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.complex,
    }),
  },

  '.left > &.enter, .right > &.exit.exit-active': {
    transform: 'translateX(100%)',
  },

  '.left > &, .right > &': {
    '&.enter.enter-active, &.exit': {
      transform: 'translateX(0)',
    },
  },

  '.right > &.enter, .left > &.exit.exit-active': {
    transform: 'translateX(-100%)',
  },
}));

export const Carousel = <TPane extends string>({ panes, children, sx }: TCarouselProps<TPane>) => {
  const [active, setActive] = useState(panes[0]);

  const { transitions } = useTheme();
  const { current: movement } = useRef({
    direction: 'left',
    active,
  });

  if (active !== movement.active) {
    movement.direction = panes.indexOf(active) > panes.indexOf(movement.active) ? 'left' : 'right';
    movement.active = active;
  }

  return (
    <Wrapper className={movement.direction}>
      <CSSTransition key={active} timeout={transitions.duration.complex}>
        <Pane sx={sx}>{children(active, setActive)}</Pane>
      </CSSTransition>
    </Wrapper>
  );
};
